import {
  ERROR_CODES,
  PAYMENT_PROVIDERS,
  PENDING_ATTEMPT_STATES,
  TERMINAL_FAILURE_STATES,
  ATTEMPT_STATES,
  VERIFICATION_OUTCOMES,
  CONNECTION_STATUS,
  PAGE_SET_PREFIX
} from "constants/powertranz"

// The guide's own form may charge a card through PowerTranz only on an active account the API calls
// payable — the same is_payable rule as useStripeIssue, so the form and the "no provider" block agree.
export const isPowerTranzGuideCardAvailable = (accountData) =>
  accountData?.connection_status === CONNECTION_STATUS.ACTIVE &&
  isPowerTranzPayable(accountData)

// PowerTranz is offered for new payments only when the API says so (is_payable, its
// powertranz_payable?), never inferred from saved settings or connection status.
// Takes the raw snake_case payload because callers read it off different guide objects.
export const isPowerTranzPayable = (accountData) => accountData?.is_payable === true


// The API decides the checkout provider (checkout_payment_provider). Only a "powertranz" answer
// overrides the checkout; anything else keeps the pre-PowerTranz rule (Stripe when it can charge,
// else PayPal) so Stripe and PayPal guests see exactly what they do today, with both groups shown.
export const resolveCheckoutProvider = ({ checkoutPaymentProvider, isStripeConnected }) => {
  if (checkoutPaymentProvider === PAYMENT_PROVIDERS.POWERTRANZ) return PAYMENT_PROVIDERS.POWERTRANZ
  return isStripeConnected ? PAYMENT_PROVIDERS.STRIPE : PAYMENT_PROVIDERS.PAYPAL
}

// The guide's own PowerTranz processing fee as grossUpCardFee takes it. PowerTranz publishes no
// rate, so the guide sets a percent in PowerTranz settings; the API sends it on the experience's
// platform_fee_setting (powertranz_card_fee_rate). Like Stripe's it is charged only while the guide
// passes card fees to guests, and it has no per-charge fixed part.
export const getPowerTranzCardFee = (platformFeeSetting) => {
  const percent = Number(platformFeeSetting?.powertranz_card_fee_rate) || 0
  const rate = percent > 0 && percent < 100 ? percent / 100 : 0
  return { rate, fixed: 0, enabled: rate > 0 && platformFeeSetting?.card_fee_passthrough_enabled === true }
}

// The API's render_error_response shape isn't pinned down in the spec, so read the code from the
// likely fields. `payload` is what api() rejects with: { data, status, ... }.
export const getPowerTranzErrorCode = (payload) => {
  const data = payload?.data || {}
  return data.code || data.error_code || (typeof data.error === "string" ? data.error : null)
}

// PowerTranz records currency as ISO 4217 numeric ("840"); Intl.NumberFormat needs the alpha code
// and throws on a numeric one. Only USD is in scope (spec), so unknown numeric codes fall back to it.
const ISO_NUMERIC_CURRENCIES = { 840: "USD" }
export const toCurrencyCode = (currency) => {
  if (ISO_NUMERIC_CURRENCIES[currency]) return ISO_NUMERIC_CURRENCIES[currency]
  return /^[A-Z]{3}$/i.test(currency || "") ? currency.toUpperCase() : "USD"
}

// User-facing message for a PowerTranz error code; unknown or missing codes get the generic one.
export const getPowerTranzErrorMessage = (t, code) =>
  code ? t(`powertranz.errors.${code}`, { defaultValue: t("powertranz.errors.generic") }) : t("powertranz.errors.generic")

export const isPendingAttemptState = (state) => PENDING_ATTEMPT_STATES.includes(state)
export const isFailedAttemptState = (state) => TERMINAL_FAILURE_STATES.includes(state)
export const isConfirmedAttemptState = (state) => state === ATTEMPT_STATES.CONFIRMED

// Normalizes a connect/verify result into one of VERIFICATION_OUTCOMES.
// Success: the outcome is what the API now reports (verified only when it says the account is
// active). Failure: the error code, else the HTTP status (no response, 5xx, 408 or 429 are worth retrying).
export const getVerificationOutcome = ({ account, error }) => {
  if (!error) {
    if (account?.connectionStatus === CONNECTION_STATUS.ACTIVE) return VERIFICATION_OUTCOMES.VERIFIED
    if (account?.connectionStatus === CONNECTION_STATUS.PENDING_VERIFICATION) return VERIFICATION_OUTCOMES.PENDING
    return VERIFICATION_OUTCOMES.FAILED
  }
  const code = getPowerTranzErrorCode(error)
  if (code === ERROR_CODES.INVALID_CREDENTIALS || code === ERROR_CODES.CREDENTIALS_UNAVAILABLE) {
    return VERIFICATION_OUTCOMES.INVALID_CREDENTIALS
  }
  if (code === ERROR_CODES.HOSTED_PAGE_NOT_FOUND) return VERIFICATION_OUTCOMES.HOSTED_PAGE_INVALID
  if (code === ERROR_CODES.GATEWAY_UNAVAILABLE || code === ERROR_CODES.MERCHANT_UNAVAILABLE || error?.status === 503) {
    return VERIFICATION_OUTCOMES.PROVIDER_UNAVAILABLE
  }
  if (!error?.status || error.status >= 500 || error.status === 408 || error.status === 429) return VERIFICATION_OUTCOMES.RETRIABLE
  return VERIFICATION_OUTCOMES.FAILED
}

// Portal-created hosted pages are addressed as "PTZ/<page set>", and the API rejects a Page Set
// without that prefix. Guides copy the bare name from the portal, so add it (fixing its case).
export const normalizePageSet = (pageSet) => {
  const trimmed = (pageSet || "").trim()
  if (!trimmed) return ""
  return trimmed.toUpperCase().startsWith(PAGE_SET_PREFIX) ? PAGE_SET_PREFIX + trimmed.slice(PAGE_SET_PREFIX.length) : PAGE_SET_PREFIX + trimmed
}

// The latest checkout attempt per booking, so a reload or a result URL without attempt_ref can
// still ask the API for that payment's status (integration guide 5.2, 6.1). attempt_ref is an
// opaque reference, not card or credential data. Storage can be unavailable (private mode), so
// every access is guarded and failing just means no recovery.
const attemptRefKey = (bookingId) => `powertranz_attempt_${bookingId}`

export const saveAttemptRef = (bookingId, attemptRef) => {
  try {
    if (bookingId && attemptRef) sessionStorage.setItem(attemptRefKey(bookingId), attemptRef)
  } catch {
    // Recovery is a convenience; checkout works without it.
  }
}

export const getSavedAttemptRef = (bookingId) => {
  try {
    return bookingId ? sessionStorage.getItem(attemptRefKey(bookingId)) : null
  } catch {
    return null
  }
}

// Where a confirmed payment takes the guest — the same booking confirmation page Stripe and PayPal
// lead to (it depends on the widget vs. signed-in client vs. guest, which the result URL can't tell).
const confirmationPathKey = (bookingId) => `powertranz_confirmation_${bookingId}`

export const saveConfirmationPath = (bookingId, path) => {
  try {
    if (bookingId && path) sessionStorage.setItem(confirmationPathKey(bookingId), path)
  } catch {
    // Without it the result page shows its own success message.
  }
}

export const getSavedConfirmationPath = (bookingId) => {
  try {
    return bookingId ? sessionStorage.getItem(confirmationPathKey(bookingId)) : null
  } catch {
    return null
  }
}

export const clearAttemptRef = (bookingId) => {
  try {
    if (!bookingId) return
    sessionStorage.removeItem(attemptRefKey(bookingId))
    sessionStorage.removeItem(confirmationPathKey(bookingId))
  } catch {
    // Nothing to clear.
  }
}
